<script>
import {giftCardsMixin} from "../../../core/data/giftcards";
import {money} from "../../../core/forms/money";

export default {
    name: "PurchaseGiftCard",
    mixins: [money, giftCardsMixin],
    components: {PhoneInput: () => import('../../custom/phone-input.vue')},
    data: () => ({
        deliveryMethods: [
            {
                name: 'sms',
                label: 'SMS',
                icon: 'fa fa-mobile'
            },
            {
                name: 'email',
                label: 'Email',
                icon: 'fa fa-envelope'
            },
            {
                name: 'whatsapp',
                label: 'WhatsApp',
                icon: 'fab fa-whatsapp'
            }
        ],
        selectedCardId: null,
    }),
    methods: {
        submit() {
            this.form.errors.clear();
            this.form.type_id = this.giftCardType.id
            this.purchase()
        },
        change(id){
            this.form.errors.clear();
            this.selectedCardId = id
        }
    },
    computed: {
        giftCard() {
            return this.giftCards.content.data[0] || {};
        },
        giftCardType() {
            const types = this.giftCard.types || [];
            return types.find(type => type.id === this.selectedCardId) || types[0] || {};
        },
        giftCardTypeInstructions() {
            const instructions = this.giftCardType.instructions || [];
            return instructions.filter(instruction => !!instruction)
        },
        giftCardTypeFeatures() {
            const features = this.giftCardType.features || [];
            return features.filter(feature => !!feature)
        }
    },
    metaInfo() {
        return {
            title: (this.giftCard.name || 'Purchase') + ' Gift Card'
        }
    },
}
</script>

<template>
    <div id="content" class="bg-light-1 py-4">
        <div class="container">
            <h2 class="title-blog text-7">{{ giftCard.name }} Gift Card</h2>
            <hr class="my-4">
            <div v-if="giftCards.loading"
                 class="text-center text-primary text-20 line-height-07 mb-5 mt-5 d-flex justify-content-center align-items-center">
                <div class="loader"></div>
            </div>
            <div class="row">
                <div class="col-lg-5">
                    <transition name="slide-in-up">
                        <img :key="giftCardType.id" class="rounded-lg shadow-sm mb-3"
                             style="width: 100%; object-fit: cover"
                             :style="{ aspectRatio: `${giftCardType.width}/${giftCardType.height}` }"
                             :src="giftCardType.image"
                             alt="">
                    </transition>
                    <div class="p-3 bg-white rounded shadow-sm mb-3">
                        <h5>{{ giftCardType.name }}</h5>
                        <p>{{ giftCardType.description }}</p>
                        <hr>
                        <div class="d-flex flex-wrap mb-3">
                            <button class="btn mb-1 mx-1 btn-sm shadow-none rounded-pill"
                                    v-for="(type, index) in giftCard.types"
                                    :key="'type'+index"
                                    :class="[giftCardType.id === type.id ? 'btn-primary': 'btn-outline-primary']"
                                    @click="change(type.id)">
                                <i class="fa fa-check-circle mr-1" v-if="giftCardType.id === type.id"></i>
                                <small class="font-weight-bold">${{ type.usd_price }}</small>
                            </button>
                        </div>
                        <hr>
                        <div class="alert alert-primary">
                            <small>You pay via <strong>EcoCash</strong></small><br>
                            <strong class="text-4">ZWG {{ money(giftCardType.zwl_price) }}</strong><br/>
                            <small>Price includes our service fee</small>
                        </div>
                    </div>
                    <div class="p-3 bg-white rounded shadow-sm" v-if="giftCardTypeFeatures.length || giftCardTypeInstructions.length">
                        <!-- Categories =============================== -->
                        <div v-if="giftCardTypeFeatures.length" class="mb-4">
                            <h4 class="text-5 fw-400">Features</h4>
                            <ul class="list-group list-group-flush">
                                <li class="list-group-item px-0" v-for="feature in giftCardTypeFeatures">
                                  &bull; {{ feature }}
                                </li>
                            </ul>
                        </div>

                        <!-- Popular Tags =============================== -->
                        <div v-if="giftCardTypeInstructions.length" class="mb-4">
                            <h4 class="text-5 fw-400">Instructions</h4>
                            <hr>
                            <ul class="list-group list-group-flush">
                                <li class="list-group-item px-0 border-0 py-1" :key="index + instruction" v-for="(instruction, index) in giftCardTypeInstructions">
                                    {{ instruction }}
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div class="col-lg-7">
                    <div class="blog-post card shadow-sm border-0 mb-4">
                        <div class="pt-4 pb-0 px-4">
                            <h5 class="mb-0">Purchase Gift Card</h5>
                        </div>
                        <hr>
                        <div class="px-4 py-2">
                            <h6 class="mb-3">Delivery Details</h6>
                            <div class="form-group">
                                <label for="phone" class="form-label">Recipient's Phone Number</label>
                                <phone-input
                                    id="phone" required
                                    aria-describedby="ecocash"
                                    v-model="form.phone"
                                    placeholder="Enter Recipient's Phone Number"></phone-input>
                                <div v-text="form.errors.get('phone')" class="text-danger"/>
                            </div>
                            <div class="form-group">
                                <label for="email" class="form-label">Recipient's Email (Optional)</label>
                                <input v-model="form.email" type="text" class="form-control" id="email" required
                                       aria-describedby="email" placeholder="Enter Recipient's Email">
                                <div v-text="form.errors.get('email')" class="text-danger"/>
                            </div>
                            <div class="form-group">
                                <label for="message" class="form-label">Recipient's Custom Message</label>
                                <small v-if="form.message.length" class="float-right text-muted">{{ 160 - form.message.length }} characters left</small>
                                <textarea v-model="form.message" class="form-control" rows="2" id="message"
                                          placeholder="Enter Custom Message" maxlength="160"></textarea>
                                <div v-text="form.errors.get('message')" class="text-danger"/>
                            </div>
                        </div>
                        <hr>
                        <div class="px-4 py-2">
                            <h6 class="mb-3">Payment Details</h6>
                            <div class="form-group">
                                <label for="ecocash" class="form-label">EcoCash Phone Number</label>
                                <phone-input id="ecocash" required="" aria-describedby="ecocash"
                                             v-model="form.ecocash" :countries="['ZW']"
                                             placeholder="Enter EcoCash Phone Number"></phone-input>
                                <div v-text="form.errors.get('ecocash')" class="text-danger"/>
                            </div>
                        </div>
                        <div class="form-group px-4">
                            <div v-text="form.errors.get('type_id')" class="text-danger mb-3"/>
                            <button type="button" class="btn btn-block btn-success mb-3"
                                    @click.prevent="submit"
                                    :class="[form.loading ? 'btn-loading' : '']">
                                Purchase Gift Card
                                <strong>(ZWG {{ giftCardType.zwl_price }})</strong>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
